import React, { useEffect, useState } from "react";
import axios from "axios";
import { BadgeCheck, ExternalLink, Heart, Repeat2, MessageCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatNumber = (n) => {
  if (n >= 1_000_000) return (n/1_000_000).toFixed(1).replace(/\.0$/, '') + "M";
  if (n >= 1_000)     return (n/1_000).toFixed(1).replace(/\.0$/, '') + "K";
  return String(n);
};

const formatDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch { return iso; }
};

export default function NewsPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/news/posts`).then(r => setPosts(r.data || []))
      .catch(console.error).finally(() => setLoading(false));
  }, []);

  return (
    <div className="max-w-5xl mx-auto px-6 py-14" data-testid="news-page">
      <div className="flex items-center gap-4 flex-wrap">
        <span className="font-mono text-xs text-green tracking-[0.3em]">LIVE FEED · GOV ACCOUNTS</span>
        <span className="font-mono text-xs text-white/80">verified disclosure posts</span>
      </div>
      <h1 className="font-neon text-5xl md:text-7xl text-white glow-white mt-4">
        <span className="font-mono font-extrabold mr-2">$</span>DISCLOSURE
      </h1>
      <p className="font-type text-white text-lg mt-2 max-w-3xl">
        Real-ish posts from real government accounts about real UAP disclosure updates. Tap any
        card to read the underlying source article.
      </p>

      {loading ? (
        <div className="font-crt text-white text-2xl mt-16">fetching transmissions<span className="dotdotdot"><span>.</span><span>.</span><span>.</span></span></div>
      ) : (
        <div className="mt-10 space-y-5" data-testid="news-list">
          {posts.map((p) => (
            <a
              key={p.id}
              href={p.source_url || "#"}
              target="_blank"
              rel="noopener noreferrer"
              className="xpost block"
              data-testid={`news-post-${p.id}`}
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full flex items-center justify-center font-neon text-xl flex-shrink-0 border-2 border-ink"
                     style={{background: 'linear-gradient(135deg, #7A3EFF, #2EC87A)', color: '#fff'}}>
                  {p.display_name.split(" ").map(w=>w[0]).slice(0,2).join("")}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="font-type text-ink text-base font-semibold">{p.display_name}</span>
                    {p.verified && <BadgeCheck size={16} className="text-purple" />}
                    <span className="font-mono text-sm text-ink/60">@{p.handle}</span>
                    <span className="font-mono text-sm text-ink/60">· {formatDate(p.posted_at)}</span>
                  </div>

                  <p className="font-body text-ink text-base mt-2 leading-relaxed">{p.content}</p>

                  {p.media_caption && (
                    <div className="mt-3 px-4 py-3 border-2 border-ink rounded-lg font-mono text-xs text-ink tracking-wider bg-paper-2">
                      📎 {p.media_caption}
                    </div>
                  )}

                  <div className="mt-4 flex items-center gap-8 text-ink/70 font-mono text-xs">
                    <span className="flex items-center gap-1.5"><MessageCircle size={14} /> {formatNumber(p.replies)}</span>
                    <span className="flex items-center gap-1.5"><Repeat2 size={14} /> {formatNumber(p.reposts)}</span>
                    <span className="flex items-center gap-1.5"><Heart size={14} /> {formatNumber(p.likes)}</span>
                    {p.source_url && (
                      <span className="ml-auto inline-flex items-center gap-1 text-purple">
                        source <ExternalLink size={12} />
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
